import * as React from 'react';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from "../app/ApplicationContext";
import {ColumnProps} from "antd/lib/table";
import {
  ErrorResponse,
  Item,
  MaintenanceHistory,
  MaintenanceHistoryResponse,
  Rental,
} from "../api/dtos/dtos";
import * as tableUtils from '../utils/table-utils';
import {GridState, searchableSortableColumn, simpleColumn, sortableColumn} from '../utils/table-utils';
import locales from "../locales";
import {Button, Table} from "antd";
import {ErrorAlert} from "../common/ErrorAlert";
import ButtonGroup from "antd/lib/button/button-group";
import furoApi from "../api/FuroApi";
import {showDeleteConfirm, showDeleteSuccessNotification, showErrorModal} from "../utils/dialogs";
import {groupNumber, renderHoursColumn, renderPriceColumn, withCurrency} from "../utils/number-utils";
import {formatDate} from "../utils/date-utils";
import SectionHeaderFormItem from "../common/form-items/SectionHeaderFormItem";
import ButtonBar from "../common/ButtonBar";
import {showPrintPopup} from "../print/PrintPopup";
import PrintMaintenanceSheetPage from "../print/PrintMaintenanceSheetPage";
import {renderMaintenanceNameCell} from "../maintenance/MaintenanceUtils";

interface Props {
  item: Item;
  onViewHistoryItem: (id: number) => void;
  onCreateHistoryItem: (item: Item, rental?: Rental) => void;
}

interface State extends GridState<MaintenanceHistory> {
  itemCount: number;
  totalCost: number;
  error?: ErrorResponse;
}

const ACTION_CELL_WIDTH = 144;

export default class MaintenanceHistoryEditor extends React.Component<Props, State> {

  public readonly loadingHandler?: LoadingOverlayHandler;

  public state: State = {
    ...tableUtils.initialGridState(),
    sortColumnKey: 'date',
    sortOrder: 'descend',
    itemCount: 0,
    totalCost: 0,
  };

  public componentDidMount() {
    this.reload();
  }

  public render(): React.ReactNode {
    const {data, error} = this.state;

    const columns: Array<ColumnProps<MaintenanceHistory>> = [
      searchableSortableColumn(locales.date, 'date', this.state, i => formatDate(i.date)),
      searchableSortableColumn(locales.appellation, 'name', this.state, renderMaintenanceNameCell),
      {
        ...simpleColumn(locales.operation_hours_reading, i => renderHoursColumn(i.operationHours)),
        className: 'align-right',
      },
      sortableColumn(locales.total_cost, 'totalCost', this.state, i => renderPriceColumn(i.totalCost)),
      {key: 'actions', width: ACTION_CELL_WIDTH, render: this.renderActionColumn}
    ];

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <SectionHeaderFormItem title={locales.maintenance_history} style={{marginTop: 24}}/>

        <ButtonBar style={{marginBottom: 8}}>
          <Button size="small" type="primary" icon="plus"
                  onClick={this.onNewHistoryItemClick}>{locales.add}</Button>
        </ButtonBar>

        <Table
          dataSource={data}
          columns={columns}
          rowKey={tableUtils.idRowKeyFn}
          bordered={true}
          size="small"
          pagination={{
            ...tableUtils.defaultPaginationConfig(this.state),
            defaultPageSize: 6,
            showSizeChanger: false,
            showTotal: undefined
          }}
          onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
          scroll={{x: 512}}
          footer={this.renderTotalsFooter}
        />

        {error && <ErrorAlert style={{marginTop: 16}} error={error}/>}
      </>
    );
  }

  private renderActionColumn = (text: any, record: MaintenanceHistory) => {
    const onEditClick = () => this.onEditClick(record);
    const onDeleteClick = () => this.onDeleteClick(record);
    const onPrintClick = () => this.onPrintClick(record);
    const onViewClick = () => this.onViewClick(record);

    return (
      <div className="action-column" style={{textAlign: 'right', whiteSpace: 'nowrap'}}>
        <ButtonGroup>
          <Button icon="printer" onClick={onPrintClick} title={locales.print}/>
          <Button icon="edit" onClick={onEditClick} title={locales.edit}/>
          <Button icon="delete" onClick={onDeleteClick} title={locales.delete}/>
          <Button icon="eye" onClick={onViewClick} title={locales.maintenance_history_view}/>
        </ButtonGroup>
      </div>
    );
  };

  private renderTotalsFooter = (): React.ReactNode => {
    const {itemCount, totalCost} = this.state;
    const width = ACTION_CELL_WIDTH - 10;
    return (
      <div style={{display: 'flex', justifyContent: 'flex-end'}}>
        <div style={{textAlign: 'right', marginRight: width}}>
          {locales.total} <b>{itemCount}</b> {locales.entry}<br/>
          <b>{withCurrency(groupNumber(totalCost))}</b>
        </div>
      </div>
    );
  };

  private onGridChange = (newState: GridState<MaintenanceHistory>) => {
    this.setState({...newState}, () => this.reload());
  };

  private reload = () => {
    this.loadMaintenanceHistory();
  };

  private loadMaintenanceHistory = () => {
    const request = tableUtils.toGridRequest(this.state);
    const promise = furoApi.items.getItemMaintenanceHistory(this.props.item.id!, request)
      .then(this.onMaintenanceHistorySuccess)
      .catch(this.onError);
    withLoadingOverlay(promise, this.loadingHandler);
  }

  private onMaintenanceHistorySuccess = (response: MaintenanceHistoryResponse) => {
    this.setState({
      ...tableUtils.toGridState(response.pagedResponse),
      itemCount: response.stats.count,
      totalCost: response.stats.total,
      error: undefined,
    });
  };

  private onEditClick = (historyItem: MaintenanceHistory) => {
    this.props.onViewHistoryItem(historyItem.id!);
  };

  private onNewHistoryItemClick = () => {
    const itemId = this.props.item.id!;
    const promise = furoApi.items.getActiveRental(itemId)
      .then(this.onActiveRentalSuccess)
      .catch(this.onError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onActiveRentalSuccess = (rental?: Rental) => {
    this.props.onCreateHistoryItem(this.props.item, rental);
  }

  private onError = (error: ErrorResponse) => {
    this.setState({error});
  };

  private onDeleteClick = (record: MaintenanceHistory) => {
    showDeleteConfirm(() => this.deleteHistoryItem(record));
  };

  private onPrintClick = (record: MaintenanceHistory) => {
    const {item} = this.props;
    showPrintPopup(locales.maintenance_worksheet, <PrintMaintenanceSheetPage historyItem={record} item={item}/>)
  }

  private onViewClick = (record: MaintenanceHistory) => {
    this.props.onViewHistoryItem(record.id!);
  }

  private deleteHistoryItem = (historyItem: MaintenanceHistory) => {
    const promise = furoApi.maintenance.deleteMaintenanceHistoryItem(historyItem.id!!)
      .then(() => {
        showDeleteSuccessNotification();
        this.reload();
      })
      .catch((e) => {
        showErrorModal(e.details.message);
      });
    withLoadingOverlay(promise, this.loadingHandler);
  };
}


// WEBPACK FOOTER //
// ./src/item-details/MaintenanceHistoryEditor.tsx